/**
 * ChannelDisplayNameField — inline editable "Display Name" input that lets a
 * tenant set the merchant-facing nickname for a sales channel (e.g. rename
 * a Shopify store to "Shopify Furniture" or an Amazon account to
 * "Amazon Wholesale"). The nickname is used by:
 *   - Order pills (`channel_display_name`)
 *   - Orders page channel filter dropdown
 *   - Dashboard "Revenue by Sales Channel" rows
 *
 * Backed by `PATCH /api/sales-channels/{id}` with `{display_name}`.
 *
 * Usage:
 *   <ChannelDisplayNameField channel={salesChannel} onSaved={refresh} />
 *
 * Pass `match` instead when you don't have the sales_channels row at hand
 * (e.g. on the ShopifyIntegrationPage where we have `store.id` but the
 * sales_channels row is keyed by `credentials_ref.store_id`):
 *   <ChannelDisplayNameField match={(c) => c.channel_type === 'shopify' &&
 *      (c.credentials_ref || {}).store_id === store.id} onSaved={refresh} />
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const token = () => localStorage.getItem('token');

const ChannelDisplayNameField = ({ channel = null, match = null, onSaved = () => {}, placeholder = 'Add a nickname (e.g. "Shopify Furniture")' }) => {
  const [resolved, setResolved] = useState(channel);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(channel?.display_name || channel?.name || '');
  const [saving, setSaving] = useState(false);

  // When the parent doesn't have the sales_channels row yet, look it up.
  useEffect(() => {
    if (channel) {
      setResolved(channel);
      setValue(channel.display_name || channel.name || '');
      return;
    }
    if (!match) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/sales-channels`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const rows = r.data.channels || r.data || [];
        const hit = rows.find((c) => {
          try { return match(c); } catch { return false; }
        });
        if (!cancelled && hit) {
          setResolved(hit);
          setValue(hit.display_name || hit.name || '');
        }
      } catch (_) {
        // Best effort — just leave the field unrendered if we can't load.
      }
    })();
    return () => { cancelled = true; };
  }, [channel, match]);

  if (!resolved) return null;

  const currentLabel = resolved.display_name || resolved.name || resolved.account_name || '';

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API_BASE_URL}/sales-channels/${resolved.id}`,
        { display_name: value || '' },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      setResolved(r.data || { ...resolved, display_name: value });
      setEditing(false);
      toast.success('Channel display name saved');
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save display name');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 text-xs text-wt-text-muted hover:text-wt-gold transition-colors"
        data-testid={`channel-rename-button-${resolved.id}`}
        title="Rename this channel"
      >
        <Pencil className="h-3 w-3" />
        <span>Display name: <span className="text-wt-text-primary">{currentLabel}</span></span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-8 w-72 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
        data-testid={`channel-rename-input-${resolved.id}`}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={save}
        disabled={saving}
        className="h-8 px-2 border-wt-border"
        data-testid={`channel-rename-save-${resolved.id}`}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => { setEditing(false); setValue(currentLabel); }}
        disabled={saving}
        className="h-8 px-2 text-wt-text-muted"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
};

export default ChannelDisplayNameField;
